import { useNavigate } from 'react-router-dom';
import type { EmployeeWithSchedule, WorkScheduleDayEntry, WeekType, Location } from '@functieloonhuis/shared';
import { DAY_LABELS, getDayEntryForDay, getAriaLabel, WORKWEEK_DAYS } from '../utils/weekUtils';

interface TeamWeekGridProps {
  employees: EmployeeWithSchedule[];
  selectedWeek: WeekType;
  filteredDay?: number | null;
  filteredLocation?: Location | null;
  showDepartment?: boolean;
  stickyHeader?: boolean;
}


function isCellDimmed(
  day: number,
  entry: WorkScheduleDayEntry | undefined,
  filteredDay: number | null | undefined,
  filteredLocation: Location | null | undefined,
): boolean {
  const hasFilters = filteredDay || filteredLocation;
  if (!hasFilters) return false;

  // Day filter: dim columns that are not the selected day
  if (filteredDay && day !== filteredDay) return true;

  // Location filter: dim cells that don't match the location
  if (filteredLocation) {
    if (!entry || !entry.isWorkday || entry.location !== filteredLocation) return true;
  }

  return false;
}

export function TeamWeekGrid({ employees, selectedWeek, filteredDay, filteredLocation, showDepartment, stickyHeader }: TeamWeekGridProps) {
  const navigate = useNavigate();
  const hasFilters = !!filteredDay || !!filteredLocation;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse" role="grid" aria-label="Team werkrooster">
        <thead className={stickyHeader ? 'sticky top-0 z-10 bg-white' : ''}>
          <tr role="row">
            <th className="text-left text-sm font-medium text-gray-600 py-2 px-3 w-48" role="columnheader">
              Medewerker
            </th>
            {showDepartment && (
              <th className="text-left text-sm font-medium text-gray-600 py-2 px-3 w-36" role="columnheader">
                Team
              </th>
            )}
            {WORKWEEK_DAYS.map(day => {
              const isHighlighted = filteredDay === day;
              return (
                <th
                  key={day}
                  className={`text-center text-sm font-medium py-2 px-1 w-[100px] ${
                    isHighlighted ? 'text-indigo-700 bg-indigo-50' : 'text-gray-600'
                  } ${hasFilters && !isHighlighted && filteredDay ? 'opacity-40' : ''}`}
                  role="columnheader"
                >
                  {DAY_LABELS[day]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {employees.map(emp => {
            const entries = selectedWeek === 'A' ? emp.weekA : emp.weekB;
            return (
              <tr key={emp.employee.id} role="row" className="hover:bg-gray-50">
                <td className="py-2 px-3">
                  <button
                    onClick={() => navigate(`/medewerkers/${emp.employee.id}?tab=werkrooster`)}
                    className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-medium text-sm">
                        {emp.employee.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {emp.employee.name}
                    </span>
                  </button>
                </td>
                {showDepartment && (
                  <td className="py-2 px-3">
                    <span className="text-sm text-gray-500 truncate">{emp.employee.departmentName || '—'}</span>
                  </td>
                )}
                {WORKWEEK_DAYS.map(day => {
                  const entry = getDayEntryForDay(entries, day);
                  const dimmed = isCellDimmed(day, entry, filteredDay, filteredLocation);
                  return (
                    <td key={day} className="py-2 px-1">
                      <CompactDayCell
                        entry={entry}
                        dayOfWeek={day}
                        employeeName={emp.employee.name}
                        dimmed={dimmed}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface CompactDayCellProps {
  entry: WorkScheduleDayEntry | undefined;
  dayOfWeek: number;
  employeeName: string;
  dimmed?: boolean;
}

function CompactDayCell({ entry, dayOfWeek, employeeName, dimmed }: CompactDayCellProps) {
  const ariaLabel = `${employeeName}, ${getAriaLabel(entry, dayOfWeek)}`;

  // Free / no entry
  if (!entry || !entry.isWorkday) {
    return (
      <div
        className={`flex items-center justify-center p-1.5 rounded bg-gray-50 border border-gray-200 h-10 ${dimmed ? 'opacity-40' : ''}`}
        role="gridcell"
        aria-label={ariaLabel}
      >
        <svg className="h-4 w-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      </div>
    );
  }

  const isKantoor = entry.location === 'kantoor';
  const bgClass = isKantoor ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';
  const iconClass = isKantoor ? 'text-green-600' : 'text-blue-600';
  const textClass = isKantoor ? 'text-green-700' : 'text-blue-700';

  const tooltipLines = [
    isKantoor ? 'Kantoor' : 'Thuis',
    entry.startTime && entry.endTime ? `${entry.startTime} - ${entry.endTime}` : null,
    entry.isHalfDay && entry.halfDayPeriod ? `Halve dag (${entry.halfDayPeriod})` : null,
  ].filter(Boolean).join('\n');

  return (
    <div
      className={`group relative flex items-center justify-center gap-1 p-1.5 rounded border h-10 ${bgClass} ${dimmed ? 'opacity-40' : ''}`}
      role="gridcell"
      aria-label={ariaLabel}
      title={tooltipLines}
    >
      {/* Icon */}
      {isKantoor ? (
        <svg className={`h-4 w-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ) : (
        <svg className={`h-4 w-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )}

      {/* Half day indicator */}
      {entry.isHalfDay && (
        <span className={`text-[9px] font-medium ${textClass}`}>½</span>
      )}
    </div>
  );
}
